import * as React from 'react'
import { formatMoney } from '../useChargebeeCheckout'
import { openBillingPortal } from '../openBillingPortal'
import { countryName } from '../countries'
import { SUPPORT_EMAIL } from './PaymentError'

function PaymentMethodLine({ paymentMethod, billingPortalUrl, site }) {
    if (!paymentMethod || !paymentMethod.last4) return null
    const brand = paymentMethod.brand
        ? paymentMethod.brand.charAt(0).toUpperCase() +
          paymentMethod.brand.slice(1)
        : 'Card'
    return (
        <div
            className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-px-3 tw-py-2.5 tw-text-sm"
            data-testid="payment-method-on-file"
        >
            <span className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-font-medium">{brand}</span>
                <span className="tw-text-muted-foreground">
                    •••• {paymentMethod.last4}
                </span>
                {paymentMethod.expiryMonth && paymentMethod.expiryYear ? (
                    <span className="tw-text-xs tw-text-muted-foreground">
                        expires{' '}
                        {String(paymentMethod.expiryMonth).padStart(2, '0')}/
                        {String(paymentMethod.expiryYear).slice(-2)}
                    </span>
                ) : null}
            </span>
            <button
                type="button"
                onClick={() =>
                    openBillingPortal({ site, fallbackUrl: billingPortalUrl })
                }
                data-testid="payment-method-change"
                className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary tw-underline-offset-4 hover:tw-underline"
            >
                Change
            </button>
        </div>
    )
}

// The plan change is taxed from the country on file. The billing portal
// offers no address editing, so a buyer who moved, or picked the wrong one
// at purchase, asks support.
function BillingCountryLine({ billingCountry }) {
    if (!billingCountry) return null
    return (
        <p
            className="tw-m-0 tw-text-sm tw-text-muted-foreground"
            data-testid="billing-country-on-file"
        >
            Billing country: {countryName(billingCountry)}. Not right?{' '}
            <a
                href={`mailto:${SUPPORT_EMAIL}`}
                data-testid="billing-country-change"
                className="tw-text-primary tw-underline-offset-4 hover:tw-underline"
            >
                Contact us
            </a>
            .
        </p>
    )
}

// The first charge of a trial falls on its end date and needs a payment
// method; closing the portal prices the change again to pick up a new one.
function PaymentMethodRequired({ billingPortalUrl, site, onPaymentMethodAdded }) {
    return (
        <div
            className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-px-3 tw-py-2.5 tw-text-sm"
            data-testid="payment-method-required"
        >
            <span>
                Add a payment method to change your plan. Nothing is charged
                until your trial ends.
            </span>
            <button
                type="button"
                onClick={() =>
                    openBillingPortal({
                        site,
                        fallbackUrl: billingPortalUrl,
                        onClose: onPaymentMethodAdded,
                    })
                }
                data-testid="payment-method-add"
                className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-text-primary tw-underline-offset-4 hover:tw-underline"
            >
                Add a payment method
            </button>
        </div>
    )
}

function OrderLine({ label, value, testId, strong, muted }) {
    return (
        <div
            className={`tw-flex tw-items-baseline tw-justify-between tw-gap-4 ${
                strong ? 'tw-font-semibold' : ''
            }`}
        >
            <span className={muted ? 'tw-text-muted-foreground' : undefined}>
                {label}
            </span>
            <span className="tw-tabular-nums" data-testid={testId}>
                {value}
            </span>
        </div>
    )
}

// Mode B answers the question the plan rows do not: is money about to move,
// how much, and off which card, and asks for an explicit yes.
export default function PlanChangeTerms({
    preview,
    previewStatus,
    planLabel,
    renewalAmount,
    renewalCurrency,
    period,
    paymentMethod,
    billingPortalUrl,
    billingCountry,
    site,
    agreed,
    onAgreedChange,
    onPaymentMethodAdded,
}) {
    const nextBilling = preview && preview.nextBillingAt
    const nextBillingDate = nextBilling
        ? new Date(nextBilling * 1000).toLocaleDateString()
        : null
    const currency = (preview && preview.currency) || renewalCurrency
    const charges = (preview && preview.lineItems) || []
    const credits = preview && preview.creditsApplied

    if (!preview) {
        return (
            <p
                className="tw-m-0 tw-text-sm tw-text-muted-foreground"
                data-testid="plan-change-terms"
            >
                {previewStatus === 'loading'
                    ? 'Working out what this change costs…'
                    : previewStatus === 'failed'
                    ? 'The cost of this change could not be worked out. Please try again.'
                    : 'Pick a different plan to see what it costs.'}
            </p>
        )
    }

    // A scheduled change charges nothing today; the new plan's first invoice on
    // the switch date is named wherever the buyer agrees to the change.
    const scheduled = preview.timing === 'end_of_term'
    const scheduledCharge = scheduled ? preview.scheduledCharge : null
    const switchDate =
        (scheduledCharge &&
            scheduledCharge.at &&
            new Date(scheduledCharge.at * 1000).toLocaleDateString()) ||
        nextBillingDate
    const scheduledAmount = scheduledCharge
        ? formatMoney(scheduledCharge.amount, scheduledCharge.currency || currency)
        : null
    const perPeriod = period === 'annual' ? 'per year' : 'per month'
    // During a trial the new plan applies now and its first charge falls on
    // the day the trial ends.
    const inTrial = preview.timing === 'trial'
    const trialEndDate =
        inTrial && preview.trialEndsAt
            ? new Date(preview.trialEndsAt * 1000).toLocaleDateString()
            : null
    const firstCharge =
        inTrial && preview.firstCharge
            ? formatMoney(
                  preview.firstCharge.amount,
                  preview.firstCharge.currency || currency
              )
            : null
    const chargedNothingToday = scheduled || inTrial
    const oncePerPeriod = period === 'annual' ? 'once per year' : 'once per month'
    const renewal = renewalAmount ? formatMoney(renewalAmount, currency) : null

    return (
        <div
            className="tw-flex tw-flex-col tw-gap-4"
            data-testid="plan-change-terms"
        >
            {/* While another plan is priced, the previous figures stay readable
                but dimmed, so they are not mistaken for the new ones. */}
            <div
                className={`tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-border tw-p-4 tw-text-sm tw-transition-opacity${
                    previewStatus === 'loading' ? ' tw-opacity-50' : ''
                }`}
                aria-busy={previewStatus === 'loading' || undefined}
                data-testid="plan-change-order"
            >
                <p className="tw-m-0 tw-font-semibold">Order details</p>
                {scheduled ? (
                    <OrderLine
                        label={`${planLabel} plan`}
                        value={
                            scheduledAmount && switchDate
                                ? `${scheduledAmount} on ${switchDate}`
                                : 'from the next period'
                        }
                        testId="plan-change-scheduled-charge"
                        muted
                    />
                ) : inTrial ? (
                    <OrderLine
                        label={`${planLabel} plan`}
                        value={
                            firstCharge && trialEndDate
                                ? `${firstCharge} on ${trialEndDate}`
                                : 'when your trial ends'
                        }
                        testId="plan-change-first-charge"
                        muted
                    />
                ) : (
                    <>
                        {charges.length ? (
                            charges.map((item, index) => (
                                <OrderLine
                                    key={`${item.description}-${index}`}
                                    label={item.description || planLabel}
                                    value={formatMoney(item.amount, currency)}
                                    muted
                                />
                            ))
                        ) : (
                            <OrderLine
                                label={`${planLabel} plan`}
                                value={formatMoney(preview.subTotal, currency)}
                                muted
                            />
                        )}
                        {(preview.discounts || []).map(discount => (
                            <OrderLine
                                key={discount.description}
                                label={discount.description || 'Discount'}
                                value={`−${formatMoney(discount.amount, currency)}`}
                                testId="plan-change-discount"
                                muted
                            />
                        ))}
                        {credits ? (
                            <OrderLine
                                label="Credit for the rest of your current plan"
                                value={`−${formatMoney(credits, currency)}`}
                                testId="plan-change-credit"
                                muted
                            />
                        ) : null}
                        {(preview.taxes || []).map(tax => (
                            <OrderLine
                                key={tax.name || tax.description}
                                label={tax.name || tax.description || 'Tax'}
                                value={formatMoney(tax.amount, currency)}
                                muted
                            />
                        ))}
                    </>
                )}
                <div className="tw-mt-1 tw-border-0 tw-border-t tw-border-solid tw-border-border tw-pt-2">
                    <OrderLine
                        label="Total due today"
                        value={formatMoney(
                            chargedNothingToday ? 0 : preview.amountDueNow,
                            currency
                        )}
                        testId="plan-change-charge"
                        strong
                    />
                </div>
            </div>

            <p className="tw-m-0 tw-text-sm tw-text-muted-foreground">
                {inTrial
                    ? `Your plan changes to ${planLabel} now, and your trial continues until ${
                          trialEndDate || 'its end date'
                      }.${
                          firstCharge
                              ? ` Then you’ll be charged ${firstCharge} ${perPeriod} until you cancel.`
                              : ''
                      } Nothing is charged today.`
                    : scheduled
                    ? `Your current plan stays active until ${
                          switchDate || 'the end of your current billing period'
                      }. Your plan will then change to ${planLabel}${
                          scheduledAmount
                              ? `, and you’ll be charged ${scheduledAmount} ${perPeriod} until you cancel`
                              : ''
                      }. Nothing is charged today.`
                    : `Your subscription renews${
                          nextBillingDate ? ` on ${nextBillingDate}` : ''
                      }${
                          renewal ? `, and you’ll then be charged ${renewal} ${perPeriod}` : ''
                      } until you cancel.`}
            </p>

            {inTrial && preview.paymentMethodRequired ? (
                <PaymentMethodRequired
                    billingPortalUrl={billingPortalUrl}
                    site={site}
                    onPaymentMethodAdded={onPaymentMethodAdded}
                />
            ) : (
                <PaymentMethodLine
                    paymentMethod={paymentMethod}
                    billingPortalUrl={billingPortalUrl}
                    site={site}
                />
            )}
            <BillingCountryLine billingCountry={billingCountry} />

            <label className="tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-text-sm tw-text-muted-foreground">
                <input
                    type="checkbox"
                    className="tw-mt-1"
                    checked={agreed}
                    onChange={event => onAgreedChange(event.target.checked)}
                    data-testid="plan-change-consent"
                />
                <span>
                    {/* "I agree" where money moves today; "I understand" where
                        nothing is charged until later. The amount today may be
                        prorated, so the renewal amount is named separately. */}
                    {inTrial
                        ? `I understand that my plan changes to ${planLabel} now, my trial continues until ${
                              trialEndDate || 'its end date'
                          }, and I’ll then be charged ${
                              firstCharge ? `${firstCharge} ` : ''
                          }${oncePerPeriod} until I cancel.`
                        : scheduled
                        ? `I understand that my plan will change to ${planLabel} at the end of my current billing period.${
                              scheduledAmount
                                  ? ` I’ll then be charged ${scheduledAmount} ${perPeriod} until I cancel.`
                                  : ''
                          }`
                        : `I agree to be charged the amount shown above today, and then ${
                              renewal ? `${renewal} ` : ''
                          }${oncePerPeriod} until I cancel. I can cancel at any time from my Subscription page.`}
                </span>
            </label>
        </div>
    )
}
